import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCalendarEvents, createCalendarEvent, deleteCalendarEvent } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, addMonths, subMonths } from 'date-fns';
import toast from 'react-hot-toast';
import { PlusIcon, ChevronLeftIcon, ChevronRightIcon, TrashIcon } from '@heroicons/react/24/outline';

const EVENT_TYPE_COLORS = {
  presentation: '#8B5CF6',
  meeting: '#10B981',
  deadline: '#EF4444',
  conference: '#3B82F6',
  workshop: '#F59E0B',
  other: '#6B7280',
};

function Modal({ onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-surface-50 border border-gray-700 rounded-xl w-full max-w-md">{children}</div>
    </div>
  );
}

export default function CalendarPage() {
  const { isFellow, isAdmin } = useAuth();
  const qc = useQueryClient();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', event_type: 'other', start_datetime: '', all_day: false, location: '', link: '' });

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  const { data } = useQuery({
    queryKey: ['calendar', format(currentMonth, 'yyyy-MM')],
    queryFn: () => getCalendarEvents({ start: monthStart.toISOString(), end: monthEnd.toISOString() }),
  });

  const createMut = useMutation({
    mutationFn: createCalendarEvent,
    onSuccess: () => { qc.invalidateQueries(['calendar']); toast.success('Event added!'); setShowModal(false); },
  });

  const deleteMut = useMutation({
    mutationFn: deleteCalendarEvent,
    onSuccess: () => { qc.invalidateQueries(['calendar']); toast.success('Event removed'); },
  });

  const events = data?.data || [];
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const eventsForDay = (day) => events.filter(e => isSameDay(new Date(e.start_datetime), day));
  const selectedDayEvents = selectedDay ? eventsForDay(selectedDay) : [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Calendar</h1>
          <p className="text-gray-500 text-sm">All fellowship events in one place</p>
        </div>
        {isFellow && (
          <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
            <PlusIcon className="w-4 h-4" /> Add Event
          </button>
        )}
      </div>

      <div className="card">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setCurrentMonth(m => subMonths(m, 1))} className="p-2 hover:bg-surface-100 rounded-lg text-gray-400 hover:text-white">
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <h2 className="font-semibold text-white">{format(currentMonth, 'MMMM yyyy')}</h2>
          <button onClick={() => setCurrentMonth(m => addMonths(m, 1))} className="p-2 hover:bg-surface-100 rounded-lg text-gray-400 hover:text-white">
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="text-center text-xs text-gray-600 font-medium py-1">{d}</div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells before month start */}
          {Array.from({ length: monthStart.getDay() }).map((_, i) => <div key={`e${i}`} />)}
          {days.map((day) => {
            const dayEvents = eventsForDay(day);
            const isSelected = selectedDay && isSameDay(day, selectedDay);
            return (
              <button
                key={day.toISOString()}
                onClick={() => setSelectedDay(isSelected ? null : day)}
                className={`min-h-[56px] rounded-lg p-1.5 text-left transition-all ${isToday(day) ? 'ring-1 ring-primary-500' : ''} ${isSelected ? 'bg-primary-600/20' : 'hover:bg-surface-100'}`}
              >
                <div className={`text-xs font-medium mb-1 ${isToday(day) ? 'text-primary-400' : 'text-gray-400'}`}>
                  {format(day, 'd')}
                </div>
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 2).map(e => (
                    <div key={e.id} className="w-full h-1.5 rounded-full" style={{ backgroundColor: EVENT_TYPE_COLORS[e.event_type] || '#6B7280' }} />
                  ))}
                  {dayEvents.length > 2 && <div className="text-xs text-gray-600">+{dayEvents.length - 2}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day events */}
      {selectedDay && (
        <div className="card">
          <h3 className="font-semibold text-white mb-3">{format(selectedDay, 'EEEE, dd MMMM yyyy')}</h3>
          {selectedDayEvents.length === 0 ? (
            <p className="text-gray-500 text-sm">No events this day.</p>
          ) : (
            <div className="space-y-2">
              {selectedDayEvents.map((e) => (
                <div key={e.id} className="flex items-start gap-3 p-3 bg-surface-100 rounded-lg">
                  <div className="w-3 h-3 rounded-full mt-0.5 flex-shrink-0" style={{ backgroundColor: EVENT_TYPE_COLORS[e.event_type] }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white">{e.title}</div>
                    <div className="text-xs text-gray-500">
                      {e.event_type} · {format(new Date(e.start_datetime), 'HH:mm')}
                      {e.location && ` · ${e.location}`}
                    </div>
                    {e.link && <a href={e.link} target="_blank" rel="noreferrer" className="text-xs text-primary-400 hover:underline">{e.link}</a>}
                  </div>
                  {(isAdmin || e.created_by?.id) && (
                    <button onClick={() => deleteMut.mutate(e.id)} className="text-red-400 hover:text-red-300 flex-shrink-0">
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-3">
        {Object.entries(EVENT_TYPE_COLORS).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1.5 text-xs text-gray-500">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
            {type}
          </div>
        ))}
      </div>

      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <div className="p-5">
            <h2 className="font-semibold text-white mb-4">Add Calendar Event</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              createMut.mutate({ ...form, start_datetime: new Date(form.start_datetime).toISOString() });
            }} className="space-y-3">
              <div><label className="text-xs text-gray-500 block mb-1">Title *</label>
                <input className="input" required value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Type</label>
                <select className="input" value={form.event_type} onChange={(e) => setForm(f => ({ ...f, event_type: e.target.value }))}>
                  {Object.keys(EVENT_TYPE_COLORS).map(t => <option key={t} value={t}>{t}</option>)}
                </select></div>
              <div><label className="text-xs text-gray-500 block mb-1">Date & Time *</label>
                <input type="datetime-local" className="input" required value={form.start_datetime} onChange={(e) => setForm(f => ({ ...f, start_datetime: e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Location</label>
                <input className="input" value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))} /></div>
              <div><label className="text-xs text-gray-500 block mb-1">Link (optional)</label>
                <input className="input" value={form.link} onChange={(e) => setForm(f => ({ ...f, link: e.target.value }))} /></div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="btn-primary flex-1" disabled={createMut.isPending}>Add Event</button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost flex-1">Cancel</button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}
